<template>
  <div v-if="returnCode">
    <h2>
      {{ 'Return Order' | t }}
      <SloLink
        v-if="authenticated"
        :to="{
          name: 'marketplace-account-return',
          params: {code: returnCode}
        }"
      >
        #{{ returnCode | formatOrderNumber }}
      </SloLink>
      <SloLink
        v-else
        :to="{
          name: 'marketplace-guest-return',
          query: {returncode: returnCode, email: order?.createdBy, billZip: order?.paymentInfo?.billingAddress?.postalCode}
        }"
      >
        #{{ returnCode | formatOrderNumber }}
      </SloLink>
    </h2>
    <p class="text-xl fw-strong mt-lg mb-sm">
      {{ 'We are reviewing your return order' | t }}
    </p>
    <p class="m-0">
      {{ 'You will receive a confirmation email with your order number and return details shortly.' | t }}
    </p>
  </div>
  <div v-else>
    <transition name="fade-up">
      <SloModal
        v-if="cancelModal"
        name="fade-up"
        :max-width="530"
        :form="false"
        @dismiss="cancelModal = false"
      >
        <template v-slot:title>{{ 'Cancel return' | t }}</template>
        <p>{{ 'Cancelling your order will return you to the order details page.' | t }}</p>
        <template v-slot:footer>
          <SloButton
            @click="handleCancel"
            type="button"
            fill="outline"
            class="flex-1"
          >
            {{ 'Yes, proceed' | t }}
          </SloButton>
          <SloButton @click="cancelModal = false" class="flex-1">
            {{ 'No, continue with return' | t }}
          </SloButton>
        </template>
      </SloModal>
    </transition>
    <div class="flex items-center justify-between flex-wrap gap-sm mb-xl md:mb-xl">
      <h1>
        {{ 'Returns' | t }}
      </h1>
      <SloButton
        v-if="review"
        @click="review = false"
        fill="clear"
        class="bg-transparent p-0"
      >
        <SloIcon name="ArrowLeft" class="mr-xs" />
        <span class="text-base fw-strong">{{ 'Back' | t }}</span>
      </SloButton>
      <SloLink
        v-if="!review && authenticated"
        :to="{
          name: 'marketplace-account-order',
          params: {code: order?.orderNumber},
          query: {back: $route.query.back}
        }"
      >
        <SloIcon name="ArrowLeft" class="mr-xs" />
        <span class="text-base fw-strong">{{ 'Back' | t }}</span>
      </SloLink>
      <SloLink
        v-if="!review && !authenticated"
        :to="{
          name: 'marketplace-guest-order',
          query: {ordercode: order?.orderNumber, email: order?.createdBy, billZip: order?.paymentInfo?.billingAddress?.postalCode}
        }"
      >
        <SloIcon name="ArrowLeft" class="mr-xs" />
        <span class="text-base fw-strong">{{ 'Back' | t }}</span>
      </SloLink>
    </div>
    <form v-if="review" action="#">
      <MarketplaceTable>
        <template v-slot:body>
          <MarketplaceRow
            v-for="(selectedEntry, index) in selectedEntries"
            :key="`returned-entry-${index}}`"
            :title="selectedEntry?.product?.number"
            :description="selectedEntry?.product?.title"
            :sku="selectedEntry?.product?.code"
            :product-type="selectedEntry?.product?.type"
            :price="selectedEntry?.basePrice?.formattedValue"
            :uom="selectedEntry?.product?.uom"
            :quantity="selectedEntry?.returnQuantity"
            :total="getReturnEntryTotalPrice(selectedEntry)"
          >
            <template v-slot:image>
              <SloProductImage :source="selectedEntry?.product?.images | getProductThumbnail" />
            </template>
          </MarketplaceRow>
        </template>
        <template v-slot:customer-return>
          <div class="customer-return--review">
            <p class="flex items-start gap-sm mb-sm">
              <span class="text-base fw-strong">{{ 'Reason' | t }}</span>
              {{ selectedReasonDisplay }}
            </p>
            <p v-if="returnNote" class="flex items-start gap-sm m-0">
              <span class="text-base fw-strong">{{ 'Note' | t }}</span>
              {{ returnNote }}
            </p>
          </div>
        </template>
        <template v-slot:footer>
          <MarketplaceTableFooter class="bg-gray-100 justify-end">
            <SloButton @click="cancelModal = true" :disabled="loading" fill="outline">
              {{ 'Cancel' | t }}
            </SloButton>
            <SloButton :disabled="loading" @click="handleReturnSubmit" class="fixed-button-width">
              <SloLoader :loading="loading" size="md" color="white">
                {{ 'Place Return' | t }}
              </SloLoader>
            </SloButton>
          </MarketplaceTableFooter>
        </template>
      </MarketplaceTable>
    </form>
    <form v-else @submit.prevent="handleReviewSubmit" action="#">
      <h3 v-if="!orderHasRemainingEntryReturnRequests" class="no-returnable-items">
        {{ 'All items in this order have been returned' | t }}
      </h3>
      <MarketplaceTable>
        <template v-slot:header v-if="orderHasRemainingEntryReturnRequests">
          <MarketplaceTableHeader>
            <template v-slot:titles>
              <SloField>
                <SloLabel class="flex items-center justify-start gap-sm">
                  <SloInput v-model="selectAll" :value="true" :checked="selectAll" type="checkbox" />
                  {{ 'Select all' | t }}
                </SloLabel>
              </SloField>
            </template>
          </MarketplaceTableHeader>
        </template>
        <template v-slot:body>
          <div
            v-for="(entry, entryIndex) in order?.entries"
            :key="`entry-key-${entryIndex}`"
          >
            <div
              v-for="(orderEntry, orderEntryIndex) in entry?.orderEntries"
              :key="`order-entry-key-${orderEntryIndex}`"
            >
              <div class="flex items-baseline gap-md lg:items-center">
                <SloInput
                  :key="`selected-entry-${orderEntryIndex}`"
                  v-if="isOrderEntryReturnable(orderEntry)"
                  :name="`select-order-entry-${orderEntryIndex}`"
                  :checked="isOrderEntrySelected(orderEntry)"
                  @input="handleEntrySelect(orderEntry, $event)"
                  type="checkbox"
                />
                <MarketplaceRow
                  :title="orderEntry?.product?.number"
                  :description="orderEntry?.product?.title"
                  :sku="orderEntry?.product?.code"
                  :product-type="orderEntry?.product?.type"
                  :price="orderEntry?.basePrice?.formattedValue"
                  :uom="orderEntry?.product?.uom"
                  :quantity="orderEntry?.quantity"
                  :total="orderEntry?.totalPrice?.formattedValue"
                  class="w-100"
                >
                  <template v-slot:image>
                    <SloProductImage :source="orderEntry?.product?.images | getProductThumbnail" />
                  </template>
                </MarketplaceRow>
              </div>
              <div class="mt-md">
                <div
                  v-if="isOrderEntrySelected(orderEntry) && isOrderEntryReturnable(orderEntry)"
                  class="flex flex-col items-end justify-end gap-sm sm:flex-row sm:items-center"
                >
                  <span class="text-base fw-book c-gray-900">
                    {{ 'How many are you returning?' | t }}
                  </span>
                  <SloNumberInput
                    :key="`selected-entry-quantity-${orderEntryIndex}`"
                    :initial-value="orderEntry?.returnQuantity"
                    :min="1"
                    :min-error-text="'Min. quantity is 1' | t"
                    :max="getRemainingEntryReturnRequests(orderEntry)"
                    :max-error-text="`Max. remaining returns for this entry is ${getRemainingEntryReturnRequests(orderEntry)}` | t"
                    @inputUpdate="handleEntryQuantityChange(orderEntry, $event)"
                    @valueUpdate="handleEntryQuantityChange(orderEntry, $event)"
                  />
                </div>
              </div>
              <p
                v-if="isEntryReturnRequestsExceeded(orderEntry)"
                class="flex items-center gap-sm mt-md mb-0 text-base fw-strong"
              >
                <span class="flex items-center justify-center c-orange-500">
                  <SloIcon name="TriangleExclamation" />
                </span>
                {{ 'Maximum quantity for this product returned' | t }}
              </p>
            </div>
          </div>
        </template>
        <template v-slot:customer-return v-if="orderHasRemainingEntryReturnRequests">
          <div class="customer-return">
            <SloField>
              <SloLabel>{{ 'Reason for return:' | t }}</SloLabel>
              <SloSelect v-model="selectedReturnReason" :placeholder="'Select return reason' | t">
                <option
                  v-for="(returnReason, index) in returnReasons"
                  :key="`return-reason-${index}`"
                  v-html="returnReason.Text"
                  :value="returnReason.Key"
                />
              </SloSelect>
            </SloField>
            <SloField>
              <SloLabel>{{ 'Return note:' | t }}</SloLabel>
              <SloTextarea v-model="returnNote" @input="limitReturnNoteInputLength" />
              <span class="text-sm fw-book c-gray-700">
                {{ returnNote ? returnNote.length : '0' }}/100 {{ 'Characters' | t }}
              </span>
              <div
                v-if="returnReasonValidationNote"
                v-html="returnReasonValidationNote"
                class="return-reason-validation-note"
              />
            </SloField>
          </div>
        </template>
        <template v-slot:footer>
          <MarketplaceTableFooter class="bg-gray-100 justify-end">
            <SloButton :disabled="loading" @click="handleCancel" fill="outline">
              {{ 'Cancel' | t }}
            </SloButton>
            <SloButton
              v-if="orderHasRemainingEntryReturnRequests"
              :disabled="formInvalid"
              type="submit"
            >
              {{ 'Continue to Review' | t }}
            </SloButton>
          </MarketplaceTableFooter>
        </template>
      </MarketplaceTable>
    </form>
  </div>
</template>

<script>
import {mapGetters} from 'vuex';
import t from '../../main/i18n';
import {marketplaceApi} from '../apis/marketplace';
import {
  calculateAndFormatPrice,
  getProductThumbnail,
  formatOrderNumber,
  handleApiErrors
} from '../../main/utils';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloLink from '../../../lib/components/SloLink.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloSelect from '../../../lib/components/SloSelect.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloTextarea from '../../../lib/components/SloTextarea.vue';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import SloNumberInput from '../../../lib/components/SloNumberInput.vue';
import SloModal from '../../../lib/components/SloModal.vue';
import MarketplaceTable from '../components/MarketplaceTable.vue';
import MarketplaceTableHeader from '../components/MarketplaceTableHeader.vue';
import MarketplaceTableFooter from '../components/MarketplaceTableFooter.vue';
import MarketplaceRow from '../components/MarketplaceRow.vue';

export default {
  name: 'OrderReturn',
  components: {
    SloLoader,
    SloInput,
    SloLink,
    SloIcon,
    SloSelect,
    SloButton,
    SloField,
    SloLabel,
    SloTextarea,
    SloProductImage,
    SloNumberInput,
    SloModal,
    MarketplaceTable,
    MarketplaceTableHeader,
    MarketplaceRow,
    MarketplaceTableFooter
  },
  filters: {
    t,
    getProductThumbnail,
    formatOrderNumber
  },
  props: {
    order: {type: Object, required: true},
    returnReasons: {type: Array, required: true}
  },
  data() {
    return {
      loading: false,
      review: false,
      selectAll: false,
      selectedEntries: [],
      selectedReturnReason: null,
      returnNote: null,
      returnCode: false,
      cancelModal: false,
      returnQuantityError: false
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    orderHasRemainingEntryReturnRequests() {
      let remainingReturnRequests = [];
      this.order?.entries.map(entry =>
        entry?.orderEntries.map(orderEntry => {
          if (this.isOrderEntryReturnable(orderEntry)) {
            remainingReturnRequests.push(orderEntry?.entryNumber);
          }
        }));

      return remainingReturnRequests.length > 0;
    },
    selectedReasonDisplay() {
      return this.returnReasons.find(reason => reason.Key === this.selectedReturnReason)?.Text;
    },
    returnReasonValidationNote() {
      return this.selectedReturnReason === 'DEFECTIVE' && !this.returnNote
        ? t('If return reason is Defective, please provide a return note')
        : false;
    },
    formInvalid() {
      return this.selectedEntries.length === 0
        || !this.selectedReturnReason
        || (this.selectedReturnReason === 'DEFECTIVE' && !this.returnNote)
        || this.returnQuantityError;
    }
  },
  watch: {
    selectAll(newValue) {
      if (newValue) {
        const selectedEntries = [];
        this.order.entries.forEach(entry =>
          entry?.orderEntries.forEach(orderEntry => {
            if (
              !this.isOrderEntrySelected(orderEntry)
              && !this.isEntryReturnRequestsExceeded(orderEntry)
              && this.isOrderEntryReturnable(orderEntry)
            ) {
              orderEntry.returnQuantity = 1;
              selectedEntries.push(orderEntry);
            }
          })
        );
        this.selectedEntries = [...selectedEntries, ...this.selectedEntries];
      } else {
        this.selectedEntries = [];
      }
    }
  },
  methods: {
    handleEntrySelect(orderEntry, value) {
      if (value) {
        orderEntry.returnQuantity = 1;
        this.selectedEntries.push(orderEntry);
      } else {
        this.selectedEntries = this.selectedEntries.filter(
          selectedEntry => selectedEntry?.entryNumber !== orderEntry?.entryNumber
        );
      }
    },
    handleEntryQuantityChange(orderEntry, value) {
      if (value < 1) {
        this.returnQuantityError = true;
        return;
      }
      if (value > this.getRemainingEntryReturnRequests(orderEntry)) {
        this.returnQuantityError = true;
        return;
      }

      this.returnQuantityError = false;
      const parsedValue = value ? parseInt(value) : 1;
      const selectedEntryIndex =
        this.selectedEntries.findIndex(entry => entry?.entryNumber === orderEntry?.entryNumber);

      this.selectedEntries[selectedEntryIndex].returnQuantity = parsedValue;
    },
    isOrderEntrySelected(orderEntry) {
      return this.selectedEntries.some(entry => entry?.entryNumber === orderEntry?.entryNumber);
    },
    isEntryReturnRequestsExceeded(orderEntry) {
      const returnRequests = orderEntry?.returnRequests
        .map(returnRequest =>
          returnRequest?.returnEntries.map(returnEntry => {
            if (returnEntry?.orderEntry?.entryNumber === orderEntry?.entryNumber) {
              return returnEntry?.expectedQuantity;
            } else {
              return 0;
            }
          })
        )
        .flat()
        .reduce((a, b) => a + b, 0);

      return returnRequests >= orderEntry?.quantity;
    },
    getRemainingEntryReturnRequests(orderEntry) {
      return orderEntry?.quantity - orderEntry?.returnRequests?.length;
    },
    limitReturnNoteInputLength(value) {
      if (value.length > 100) {
        this.returnNote = value.substring(0, 100);
      }
    },
    async handleReviewSubmit() {
      this.review = true;
      window.scrollTo(0, 0);
    },
    async handleReturnSubmit() {
      this.loading = true;

      const returnRequestEntryInputs = this.selectedEntries.map(entry => {
        return {
          notes: this.returnNote,
          orderEntryNumber: entry?.entryNumber,
          quantity: entry?.returnQuantity,
          reason: this.selectedReturnReason
        };
      });

      await marketplaceApi.placeReturn(
        this.order?.paymentInfo?.billingAddress?.postalCode,
        this.order?.createdBy,
        this.order?.orderNumber,
        returnRequestEntryInputs
      )
        .then(async res => {
          if (res?.data.length) {
            this.loading = false;
            this.returnCode = res.data;
          } else {
            this.$snotify.error(t('Unable to place return request'));
            await this.$router.back();
          }
        })
        .catch(e => {
          this.loading = false;
          handleApiErrors(e, this, 'Unable to place return request');
        });
    },
    getReturnEntryTotalPrice(selectedEntry) {
      return calculateAndFormatPrice(
        selectedEntry?.product?.price?.value,
        selectedEntry?.returnQuantity
      );
    },
    isOrderEntryReturnable(orderEntry) {
      return !this.isEntryReturnRequestsExceeded(orderEntry)
        && orderEntry?.product?.isReturnable;
    },
    handleCancel() {
      if (this.authenticated) {
        this.$router.replace({
          name: 'marketplace-account-order',
          params: {code: this.order?.orderNumber},
          query: {back: this.$route.query.back}
        });
        return;
      }

      this.$router.replace({
        name: 'marketplace-guest-order',
        query: {
          ordercode: this.order?.orderNumber,
          email: this.order?.createdBy,
          billZip: this.order?.paymentInfo?.billingAddress?.postalCode
        }
      });
    }
  }
};
</script>

<style scoped lang="scss">
@import '../../../scss/variables';
@import '../../../scss/system';

h1, h2 {
  font-weight: normal;
  font-size: 3.6rem;
  margin: 0;

  @media (min-width: $screen-sm) {
    @include text('3xl');
  }
}

.customer-return {
  display: grid;
  gap: 1.6rem;
  padding: 3.2rem 1.6rem;
  border-top: 1px solid $color-gray-400;

  @media (min-width: $screen-sm) {
    grid-template-columns: 1fr 1fr;
    gap: 2.4rem;
    padding: 3.2rem;
  }

  :deep(.slo-input.type-text) {
    textarea {
      margin: 0 !important;
    }
  }

  &--review {
    padding: 1.6rem;
    background-color: #fffbeb;
    border-top: 1px solid $color-gray-400;
  }
}

.no-returnable-items {
  margin: 0 0 40px 0;
  color: $color-red-400;
  font-weight: $font-weight-medium;
}

.fixed-button-width {
  flex-basis: 118px;
}

.return-reason-validation-note {
  margin: 0 0 1.6rem 0;
  color: $color-red-400 !important;
}
</style>
